import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { TrendingUp, TrendingDown, Minus, Download, Copy } from 'lucide-react';
import Button from '../Button';

interface StackedDataItem {
  label: string;
  value: number;
  color: string;
}

interface HeatmapData {
  category?: string;
  name?: string;
  value: string;
  percentage?: string;
  breakdown?: string;
  ranking?: number;
  score?: number;
  stacked_data?: StackedDataItem[];
}

interface Visualization {
  type: 'heatmap';
  title: string;
  data: HeatmapData[];
}

interface HeatmapChartProps {
  visualization: Visualization;
  questionType?: string;
  className?: string;
}

const HeatmapChart: React.FC<HeatmapChartProps> = ({ 
  visualization, 
  questionType = 'default',
  className = '' 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  
  if (!visualization?.data || visualization.data.length === 0) return null;

  const handleCopy = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, { 
        backgroundColor: '#fff',
        useCORS: true,
        height: chartRef.current.scrollHeight,
        windowHeight: chartRef.current.scrollHeight
      });
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, { 
      backgroundColor: '#fff', 
      scale: 2,
      useCORS: true,
      height: chartRef.current.scrollHeight,
      windowHeight: chartRef.current.scrollHeight
    });
    const link = document.createElement('a');
    link.download = 'heatmap-chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const isMatrix = questionType && ['matrix_rating', 'matrix_grid'].includes(questionType);

  // Check if this is ranking data with position breakdown
  const hasRankingData = visualization.data[0]?.stacked_data;
  
  if (!hasRankingData) {
    // For non-ranking questions, show simple grid
    return (
      <div className={`bg-white p-4 rounded-lg border border-gray-200 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">{visualization.title}</h4>
          <div className="flex gap-2 no-print">
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                icon={<Download className="h-4 w-4" />}
                onClick={handleDownload}
                title="Download chart as image"
                onMouseEnter={() => setShowTooltip('download')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                {''}
              </Button>
              {showTooltip === 'download' && (
                <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                  Download chart as image
                </div>
              )}
            </div>
            <div className="relative">
              <Button
                size="sm"
                variant="ghost"
                icon={<Copy className="h-4 w-4" />}
                onClick={handleCopy}
                title="Copy chart as image"
                onMouseEnter={() => setShowTooltip('copy')}
                onMouseLeave={() => setShowTooltip(null)}
              >
                {''}
              </Button>
              {showTooltip === 'copy' && (
                <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                  Copy chart as image
                </div>
              )}
            </div>
            {copied && (
              <span className="text-green-600 text-xs ml-1">Copied!</span>
            )}
          </div>
        </div>
        
        <div ref={chartRef} className="w-full h-full pb-8">
          <div className="text-sm text-gray-600 mb-4">
            Simple response distribution (no ranking data available)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {visualization.data.map((item, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="font-medium text-gray-900 text-sm mb-1">{item.category || item.name}</div>
              <div className="text-lg font-bold text-blue-600">{item.value}</div>
              {item.percentage && (
                <div className="text-xs text-gray-600">{item.percentage}</div>
              )}
            </div>
          ))}
          </div>
        </div>
      </div>
    );
  }

  // Extract dynamic ranking positions from the actual data
  const extractRankingPositions = (data: HeatmapData[]) => {
    const positions = new Set<string>();
    data.forEach(item => {
      item.stacked_data?.forEach(stack => {
        positions.add(stack.label);
      });
    });
    return Array.from(positions).sort((a, b) => {
      // Sort by position number (1st, 2nd, 3rd, 4th, etc.)
      const aNum = parseInt(a.match(/\d+/)?.[0] || '0');
      const bNum = parseInt(b.match(/\d+/)?.[0] || '0');
      return aNum - bNum;
    });
  };

  const rankingPositions = extractRankingPositions(visualization.data);
  const options = visualization.data.map(d => d.category || d.name || '');
  
  // Create matrix data: [option][position] = count
  const matrixData = visualization.data.map(item => {
    const rankings: Record<string, number> = {};
    item.stacked_data?.forEach(stack => {
      rankings[stack.label] = stack.value;
    });
    return {
      option: item.category || item.name || '',
      rankings
    };
  });

  // Find max value for color intensity
  const allRankingValues = matrixData.flatMap(item => 
    rankingPositions.map(pos => item.rankings[pos] || 0)
  );
  const maxRankingValue = Math.max(...allRankingValues);

  // Color intensity function based on ranking count
  const getIntensity = (value: number) => {
    return maxRankingValue > 0 ? value / maxRankingValue : 0;
  };

  // Get color based on position and intensity - BluePill AI brand color palette
  const getColor = (position: string, intensity: number) => {
    const alpha = 0.2 + intensity * 0.7; // More visible range
    const baseColors = [
      [7, 229, 209],     // Primary Teal (#07E5D1) for 1st
      [6, 212, 194],     // Primary Hover (#06D4C2) for 2nd  
      [16, 185, 129],    // Success Green (#10B981) for 3rd
      [59, 130, 246],    // Info Blue (#3B82F6) for 4th
      [245, 158, 11],    // Warning Amber (#F59E0B) for 5th
      [239, 68, 68],     // Error Red (#EF4444) for 6th
      [28, 28, 30],      // Text Primary (#1C1C1E) for 7th
      [55, 65, 81],      // Text Medium (#374151) for 8th
    ];
    
    // Extract position number from label (e.g., "1st Place" -> 1)
    const positionNum = parseInt(position.match(/\d+/)?.[0] || '1');
    const colorIndex = ((positionNum - 1) % baseColors.length + baseColors.length) % baseColors.length;
    const [r, g, b] = baseColors[colorIndex];
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className={`bg-white p-6 rounded-lg border border-gray-200 ${className}`}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">{visualization.title}</h4>
          <p className="text-sm text-gray-600">
            {questionType && ['matrix_rating', 'matrix_grid'].includes(questionType)
              ? 'Heatmap showing rating distribution for each factor'
              : 'Heatmap showing how many people ranked each option in different positions'}
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Download className="h-4 w-4" />}
              onClick={handleDownload}
              title="Download chart as image"
              onMouseEnter={() => setShowTooltip('download')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'download' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Download chart as image
              </div>
            )}
          </div>
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Copy className="h-4 w-4" />}
              onClick={handleCopy}
              title="Copy chart as image"
              onMouseEnter={() => setShowTooltip('copy')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'copy' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Copy chart as image
              </div>
            )}
          </div>
          {copied && (
            <span className="text-green-600 text-xs ml-1">Copied!</span>
          )}
        </div>
      </div>

      <div ref={chartRef} className="w-full h-full pb-8">
        {/* Legend */}
        <div className="mb-6 flex flex-wrap gap-4 justify-center">
        {rankingPositions.map((position, index) => (
          <div key={index} className="flex items-center space-x-2 px-3 py-1 bg-gray-50 rounded-full">
            <div 
              className="w-3 h-3 rounded" 
              style={{ backgroundColor: getColor(position, 0.8) }}
            />
            <span className="text-sm font-medium text-gray-700">{position}</span>
          </div>
        ))}
      </div>

      {/* Heatmap Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header Row */}
          <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: `200px repeat(${rankingPositions.length}, 1fr)` }}>
            <div className="p-3 font-semibold text-gray-900 bg-gray-50 rounded-lg">
              {isMatrix ? 'Factor' : 'Option'}
            </div>
            {rankingPositions.map((position, index) => (
              <div key={index} className="p-3 font-semibold text-gray-700 bg-gray-50 rounded-lg text-center">
                {isMatrix ? `Rating ${position}` : position}
              </div>
            ))}
          </div>

          {/* Data Rows */}
          {matrixData.map((item, rowIndex) => (
            <div key={rowIndex} className="grid gap-2 mb-2" style={{ gridTemplateColumns: `200px repeat(${rankingPositions.length}, 1fr)` }}>
              {/* Option Name */}
              <div className="p-3 font-medium text-gray-900 bg-white border border-gray-200 rounded-lg flex items-center">
                <span className="truncate">{item.option}</span>
              </div>
              
              {/* Ranking Values */}
              {rankingPositions.map((position, colIndex) => {
                const value = item.rankings[position] || 0;
                const intensity = getIntensity(value);
                
                return (
                  <div
                    key={colIndex}
                    className="p-4 border border-gray-200 rounded-lg text-center relative group transition-all duration-200 hover:scale-105"
                    style={{
                      backgroundColor: getColor(position, intensity),
                      minHeight: '60px'
                    }}
                  >
                    <div className="text-xl font-bold text-gray-900">
                      {value}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {value === 1 ? 'person' : 'people'}
                    </div>
                    
                    {/* Hover tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20 whitespace-nowrap">
                      {isMatrix
                        ? `${value} people rated "${item.option}" as ${position}`
                        : `${value} people ranked "${item.option}" as ${position}`}
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Summary Statistics - keeping useful metrics, removing total rankings */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-sm text-gray-600">{isMatrix ? 'Highest Rated Factor' : 'Highest Weighted Score'}</div>
            <div className="text-lg font-semibold" style={{ color: '#07E5D1' }}>
              {matrixData.reduce((max, item) => {
                // Calculate weighted score dynamically
                // For matrix ratings: higher ratings (5) get more weight
                // For rankings: lower positions (1st) get more weight
                let itemScore = 0;
                let maxScore = 0;

                rankingPositions.forEach((pos, index) => {
                  const weight = isMatrix
                    ? index + 1  // Rating "1" gets weight 1, "5" gets weight 5
                    : rankingPositions.length - index;  // 1st place gets highest weight
                  itemScore += (item.rankings[pos] || 0) * weight;
                  maxScore += (max.rankings[pos] || 0) * weight;
                });

                return itemScore > maxScore ? item : max;
              }, matrixData[0])?.option}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-600">{isMatrix ? 'Most Top Ratings' : 'Most 1st Place Votes'}</div>
            <div className="text-lg font-semibold" style={{ color: '#07E5D1' }}>
              {matrixData.reduce((max, item) => {
                // For matrix: top rating is the last one (e.g., "5")
                // For ranking: top is first one (e.g., "1st Place")
                const topRatingKey = isMatrix
                  ? rankingPositions[rankingPositions.length - 1]
                  : rankingPositions[0];
                return (item.rankings[topRatingKey] || 0) > (max.rankings[topRatingKey] || 0) ? item : max;
              }, matrixData[0])?.option}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default HeatmapChart;